/**
 * @file Parses a stored `analyticsReports/{reportId}` document into `AnalyticsReportDefinition`, or
 * names the first malformed field: an unknown top-level key, `name`, `level`, `period`, `queries`,
 * each query, a query's own `period`, its `query` outside `ANALYTICS_REPORT_QUERY_IDS`, its `parameters` and any
 * parameter beyond `NAMED_QUERY_PARAMETER_ENUMS` under `queries.<i>`, `cadence` and its fields,
 * `recipientUids` and each uid, then `nextRunAt`, which must be a `Date#toISOString()` string. The
 * cadence is parsed on its own and returned with a malformed result, so `runDueReports` can still
 * advance a definition whose cadence is sound and stop one whose cadence is not. It lives in
 * `types/src` so React's `createAnalyticsReport` writes only what `runDueReports` accepts; a
 * `cloud_run/analytics` test ties its query check to `parseNamedQueryRequest` and `NAMED_QUERIES`.
 */
import { ANALYTICS_PERIODS, ANALYTICS_SCOPE_LEVELS, NAMED_QUERY_PARAMETER_ENUMS } from './Analytics.NamedQuery.types';
import { ANALYTICS_REPORT_QUERY_IDS } from './ANALYTICS_REPORT_QUERY_IDS';
import { ANALYTICS_REPORT_CADENCES } from './Analytics.Report.types';
import type { AnalyticsReportCadence, AnalyticsReportDefinition, AnalyticsReportQuery } from './Analytics.Report.types';
import { isIsoInstant } from './isIsoInstant';

type ParsedAnalyticsReportDefinition =
    | { status: 'valid'; definition: AnalyticsReportDefinition & { nextRunAt: string } }
    | { status: 'malformed'; reason: string; name: string | null; cadence: AnalyticsReportCadence | null };

const DEFINITION_KEYS: readonly string[] = ['name', 'queries', 'level', 'period', 'cadence', 'recipientUids', 'nextRunAt'] satisfies (keyof AnalyticsReportDefinition)[];
const QUERY_KEYS: readonly string[] = ['query', 'parameters'] satisfies (keyof AnalyticsReportQuery)[];
const CADENCE_KEYS: readonly string[] = ['every', 'hour'] satisfies (keyof AnalyticsReportCadence)[];
const HOURS_PER_DAY = 24;

const fieldsOf = (value: unknown): Record<string, unknown> | undefined =>
    value === null || typeof value !== 'object' || Array.isArray(value) ? undefined : Object.fromEntries(Object.entries(value));

const parseCadence = (value: unknown): AnalyticsReportCadence | string => {
    const cadence = fieldsOf(value);
    if (cadence === undefined || Object.keys(cadence).some((key) => !CADENCE_KEYS.includes(key))) return 'cadence';
    const every = ANALYTICS_REPORT_CADENCES.find((candidate) => candidate === cadence.every);
    if (every === undefined) return 'cadence.every';
    const { hour } = cadence;
    if (typeof hour !== 'number' || !Number.isInteger(hour) || hour < 0 || hour >= HOURS_PER_DAY) return 'cadence.hour';
    return { every, hour };
};

const parseQuery = (value: unknown, index: number): AnalyticsReportQuery | string => {
    const entry = fieldsOf(value);
    if (entry === undefined || Object.keys(entry).some((key) => !QUERY_KEYS.includes(key))) return `queries.${index}`;
    const parameters = fieldsOf(entry.parameters);
    if (parameters !== undefined && 'period' in parameters) return `queries.${index}.parameters.period`;
    const query = ANALYTICS_REPORT_QUERY_IDS.find((id) => id === entry.query);
    if (query === undefined) return `queries.${index}.query`;
    if (parameters === undefined) return `queries.${index}.parameters`;
    const extraKey = Object.keys(parameters).find((key) => !Object.keys(NAMED_QUERY_PARAMETER_ENUMS).includes(key));
    if (extraKey !== undefined) return `queries.${index}.parameters.${extraKey}`;
    return { query, parameters: {} };
};

export function parseAnalyticsReportDefinition(data: unknown): ParsedAnalyticsReportDefinition {
    const fields = fieldsOf(data) ?? {};
    const parsedCadence = parseCadence(fields.cadence);
    const cadence = typeof parsedCadence === 'string' ? null : parsedCadence;
    const name = typeof fields.name === 'string' && fields.name !== '' ? fields.name : null;
    const malformed = (reason: string): ParsedAnalyticsReportDefinition => ({ status: 'malformed', reason, name, cadence });

    if (fieldsOf(data) === undefined) return malformed('definition');
    const unknownKey = Object.keys(fields).find((key) => !DEFINITION_KEYS.includes(key));
    if (unknownKey !== undefined) return malformed(unknownKey);
    if (name === null) return malformed('name');
    const level = ANALYTICS_SCOPE_LEVELS.find((candidate) => candidate === fields.level);
    if (level === undefined) return malformed('level');
    const period = ANALYTICS_PERIODS.find((candidate) => candidate === fields.period);
    if (period === undefined) return malformed('period');
    if (!Array.isArray(fields.queries) || fields.queries.length === 0) return malformed('queries');
    const queries = fields.queries.map((query: unknown, index) => parseQuery(query, index));
    const queryReason = queries.find((query) => typeof query === 'string');
    if (queryReason !== undefined) return malformed(queryReason);
    if (typeof parsedCadence === 'string') return malformed(parsedCadence);
    const { recipientUids } = fields;
    if (
        !Array.isArray(recipientUids) ||
        recipientUids.length === 0 ||
        !recipientUids.every((uid): uid is string => typeof uid === 'string') ||
        new Set(recipientUids).size !== recipientUids.length
    ) {
        return malformed('recipientUids');
    }
    const badUid = recipientUids.findIndex((uid) => uid === '' || uid.includes('/'));
    if (badUid !== -1) return malformed(`recipientUids.${badUid}`);
    if (!isIsoInstant(fields.nextRunAt)) return malformed('nextRunAt');
    return {
        status: 'valid',
        definition: {
            name,
            queries: queries.filter((query) => typeof query !== 'string'),
            level,
            period,
            cadence: parsedCadence,
            recipientUids,
            nextRunAt: fields.nextRunAt,
        },
    };
}
